import { Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons';
import { useCallback } from 'react';
import styled from 'styled-components';

import { ModalContainer } from 'dashboard/layouts/ModalLayout';
import { Z_INDICES } from 'dashboard/styles/variables';
import WorkflowsJsonVersionDiff, {
  DiffConfig,
} from 'dashboard/views/DashboardWorkflowsShowView/WorkflowsJsonVersionDiff';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { Heading, Paragraph } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';

const HEADER_HEIGHT = 71;
const FOOTER_HEIGHT = 56;
const WorkflowVersionDiffModalContainer = styled(ModalContainer)`
  height: clamp(800px, 80vh, 1300px);
  width: clamp(1150px, 90vw, 1700px);
  padding: 0;
`;

const DiffModalHeader = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: ${HEADER_HEIGHT}px;
  max-height: ${HEADER_HEIGHT}px;
  padding: ${Spacing[2]} ${Spacing[5]};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
  z-index: ${Z_INDICES.modalElevated};
`;

const HeaderMainRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const CloseButton = styled.button.attrs(() => ({
  type: 'button',
}))`
  cursor: pointer;
  margin-left: auto;
  background: none;
  border: 0;
`;

const ModalFooter = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  margin-top: auto;
  height: ${FOOTER_HEIGHT}px;
  padding: ${Spacing[4]};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
`;

interface WorkflowVersionDiffModalProps {
  diffConfigs: DiffConfig[];
  baseVersionId?: string;
  headerText?: string;
  loading: boolean;
  targetVersionId: string;
  handlePublish: () => void;
  handleClose: () => void;
}

const WorkflowVersionDiffModal = ({
  diffConfigs,
  baseVersionId,
  targetVersionId,
  loading,
  headerText,
  handlePublish,
  handleClose,
}: WorkflowVersionDiffModalProps) => {
  const [isPending, { enable, disable }] = useToggle(false);

  const handleConfirm = useCallback(async () => {
    enable();
    handlePublish();
    disable();
    handleClose();
  }, [disable, enable, handleClose, handlePublish]);

  return (
    <WorkflowVersionDiffModalContainer>
      <DiffModalHeader>
        <HeaderMainRow>
          <Heading size='sm'>Review your changes</Heading>
          <CloseButton
            id='diff-modal__close-button'
            data-test='diff-modal__close-button'
            onClick={handleClose}
          >
            <XIcon size={24} />
          </CloseButton>
        </HeaderMainRow>
        <Paragraph size='md'>
          Please review your changes, as publishing will affect any future production workflows
          created using this template.
        </Paragraph>
      </DiffModalHeader>
      <WorkflowsJsonVersionDiff
        baseVersionId={baseVersionId}
        diffConfigs={diffConfigs}
        headerText={headerText}
        targetVersionId={targetVersionId}
        loading={loading}
      />
      <ModalFooter data-test='diff-modal__footer'>
        <HStack spacing={Spacing[4]}>
          <Button
            variant='basic'
            aria-label='Cancel'
            id='diff-modal__cancel-button'
            isPending={isPending || loading}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            aria-label='Publish'
            data-test='diff-modal__publish-button'
            id='diff-modal__publish-button'
            data-cy='diff-modal__publish-button'
            isPending={isPending || loading}
            onClick={handleConfirm}
          >
            Confirm Changes
          </Button>
        </HStack>
      </ModalFooter>
    </WorkflowVersionDiffModalContainer>
  );
};

export default WorkflowVersionDiffModal;
